import * as chatSelectors from 'app-modules/chat/selectors';
import * as viralSelectors from 'app-modules/viral/selectors';
import { properties as embedSelector } from 'app-modules/embed/selectors';
import { TYPE as EMBED_TYPE } from 'app-modules/embed/constants';
import {
  isEnabled as enableViral,
  playViralClip,
  changeViralUrlSlug,
} from 'app-modules/viral/actions';
import { actions, constants, selectors } from 'app-webtech-core';
import { connect } from 'react-redux';
import parse from 'url-parse';
import {
  ccMenuClose,
  ccMenuOpen,
  ccMenuReset,
  ccMenuSetIndex,
  ccNextSubmenu,
  enterFullscreen,
  exitFullscreen,
  internalEnteredFullscreen,
  internalExitedFullscreen,
  toggleCaption,
} from '../../player/actions';

import { PlayerComponent } from '../components/Player';
import { UI_STATES } from '../../ui/reducers';
import { changeUrlSlug as changeChannelUrlSlug } from '../../guide/actions';
import { playerContentUrlSelector } from '../selectors/contentUrl';

const VIRAL_DEFAULT_FALLBACK_CHANNEL = 1;
const PLAYING_STATUSES = [
  constants.player.PLAYER_STATE_STATUSES.PLAYING,
  constants.player.PLAYER_STATE_STATUSES.PAUSED,
];

const mapStateToProps = (state, ownProps) => {
  const player = selectors.player.properties(state);
  const content = selectors.player.content(state);
  const embedState = embedSelector(state);
  const isEmbed = embedState.enabled || embedState.corpEmbed;
  const isHeadless = isEmbed && embedState.type === EMBED_TYPE.EMBED_TYPE_HEADLESS;
  const kind = selectors.player.kind(state);
  const page = selectors.location.page(state);
  const { currentPlayingIndex, list = [], episodeDialogInfo } = selectors.guide.properties(state);
  const currentPlayingChannelId = list[currentPlayingIndex] && list[currentPlayingIndex].id;
  const { query } = selectors.location.properties(state);
  const fetchBySlug = selectors.linearChannels.fetchBySlug(state);
  const queryChannel = query && query.channel ? fetchBySlug(query.channel) : null;
  const nextChannel = queryChannel ? list.find((item) => item.id === queryChannel.id) : null;
  const nextChannelIndex = nextChannel ? nextChannel.index : null;
  const isViral = kind === constants.player.PLAYER_CONTENT_KIND.VIRAL;
  const { clips } = viralSelectors.properties(state).content;
  const linearChannels = selectors.linearChannels.properties(state);
  const { infoBanner } = player;
  const isEpisode = Boolean(
    infoBanner &&
      infoBanner.vodContent &&
      infoBanner.vodContent.type === constants.vodContent.VOD_CONTENT_TYPE.EPISODE,
  );
  const hidePrivacyNotification = state.applicationinternal.hidePrivacyNotification;

  let viralNextChannel;
  if (list.length > 0) {
    const viralNextCh = nextChannelIndex
      ? list[nextChannelIndex]
      : list[VIRAL_DEFAULT_FALLBACK_CHANNEL];
    viralNextChannel =
      linearChannels &&
      linearChannels.entities &&
      linearChannels.entities.linearChannels &&
      viralNextCh
        ? linearChannels.entities.linearChannels[viralNextCh.id]
        : {};
    viralNextChannel.guideIndex = nextChannelIndex;
  }

  const nextEpisode = isEpisode ? selectors.vodContent.nextEpisode(state)(content.id) : null;

  const isChatBarShown = chatSelectors.isVisible(state);

  const url = playerContentUrlSelector(state);

  const isPageTrending = page === 'Trending';

  let featureImageUrl;
  if (infoBanner && infoBanner.channel && infoBanner.channel.featuredImage) {
    const parsedFeaturedImage = parse(infoBanner.channel.featuredImage);
    parsedFeaturedImage.set('query', '?w=800&h=450&fm=jpg&q=75&fit=fill&fill=blur');
    featureImageUrl = parsedFeaturedImage.toString();
  } else if (isViral) {
    const item = clips.find((item) => item._id === player.content.id);
    // The last element in clips is always the initial clip launched by slug in the url
    const initialViralClip = clips[clips.length - 1];
    featureImageUrl =
      player.content &&
      player.content.id === initialViralClip._id &&
      initialViralClip.thumbnail.toLowerCase().indexOf(initialViralClip.slug) > 0
        ? initialViralClip.thumbnail
        : item
        ? item.thumbnail
        : '/assets/images/bg-sharp-1x.jpg';
  }

  return {
    kind,
    url,
    isEmbed,
    isHeadless,
    canTouch: state.ui.canTouch,
    featuredImage: featureImageUrl,
    isEpisode,
    episodeDialogInfo,
    isViral,
    viralClips: clips,
    viralNextChannel,
    query,
    nextChannelIndex,
    isPaused: player.status === 'PAUSED',
    isStopped: player.status === 'STOPPED',
    nextEpisode,
    internalFullscreen: state.playerinternal.fullscreen || !ownProps.guideVisible,
    isPlaying: player.status === constants.player.PLAYER_STATE_STATUSES.PLAYING,
    isPausedOrPlaying: PLAYING_STATUSES.includes(player.status),
    isBuffering: player.status === constants.player.PLAYER_STATE_STATUSES.BUFFERING,
    nowPlaying: player.nowPlaying,
    playerIsFullscreen:
      player.isFullscreen || state.playerinternal.fullscreen || !ownProps.guideVisible,
    isDocked: !player.isFullscreen,
    uiControls: state.ui.state === UI_STATES.CONTROLS,
    hasError: player.status === constants.player.PLAYER_STATE_STATUSES.ERROR,
    hidePlayer:
      (page === 'Vod' && !isViral && !content) ||
      isPageTrending ||
      (!player.isFullscreen && isEmbed),
    isPageTrending,
    isChatBarShown,
    isMuted: player.mute,
    volume: player.volume,
    volumeBeforeMute: player.volumeBeforeMute,
    isCCMenuOpen: player.isCCMenuOpen,
    currentCCMenu: player.currentCCMenu,
    currentCCMenuName: player.currentCCMenuName,
    ccMenuSelectedIndexes: player.ccMenuSelectedIndexes,
    currentPlayingChannelId,
    hidePrivacyNotification,
  };
};

const mapDispatchToProps = {
  clearError: actions.player.clearError,
  enteredFullscreen: enterFullscreen,
  exitedFullscreen: exitFullscreen,
  ccSubmenuOpened: ccNextSubmenu,
  ccMenuOpened: ccMenuOpen,
  ccMenuClosed: ccMenuClose,
  ccMenuOnReset: ccMenuReset,
  ccSetIndex: ccMenuSetIndex,
  internalEnteredFullscreen,
  internalExitedFullscreen,
  toggleCaption,
  playViralClip,
  changeViralUrlSlug,
  mute: actions.player.mute,
  onBuffer: actions.player.startBuffering,
  onError: actions.player.setError,
  unmute: actions.player.unmute,
  vodPlayItem: actions.vodGuide.playItem,
  volumeChange: actions.player.volumeChange,
  play: actions.player.play,
  pause: actions.player.pause,
  stop: actions.player.stop,
  clickPlay: actions.player.clickPlay,
  clickPause: actions.player.clickPause,
  uiLoaded: actions.layout.uiLoaded,
  changeChannelUrlSlug,
  playChannelFullscreen: actions.guide.playChannelFullscreen,
  playChannelAtIndex: actions.guide.playChannelAtIndex,
  closeEpisodeDialog: actions.guide.hideEpisodeInfoDialog,
  enableViral,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  playTVChannel: () => {
    dispatchProps.playChannelFullscreen({ index: 1, setSelected: true });
  },
  playTVChannelAtIndex: (index) => {
    dispatchProps.enableViral(false);
    dispatchProps.playChannelAtIndex({
      index,
      setSelected: true,
    });
  },
  playNewsChannel: () => {
    dispatchProps.enableViral(false);
    dispatchProps.playChannelAtIndex({
      index: VIRAL_DEFAULT_FALLBACK_CHANNEL,
      setSelected: true,
    });
  },
  dispatchPlayViralClip: (id, slug) => () => {
    dispatchProps.playViralClip({ id, slug });
  },
  changeViralUrl: (slug) => dispatchProps.changeViralUrlSlug(slug),
  closeEpisodeDialog: () => dispatchProps.closeEpisodeDialog(),
});

export const Player = connect(mapStateToProps, mapDispatchToProps, mergeProps)(PlayerComponent);
